import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FolderOpen } from 'lucide-react';
import { xamplinApi } from '@/lib/api/xamplin';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { usePagination } from '@/lib/hooks/usePagination';
import { Pagination } from '@/components/ui/Pagination';

/**
 * Projects list (build-v2 F4). Real xp-projects for the active brand → planner.
 */
export function ProjectsListPage() {
  const selectedBrandId = useSelectedBrandId();

  const brandsQ = useQuery({ queryKey: queryKeys.brands.all, queryFn: () => brandsApi.list() });
  const projectsQ = useQuery({
    queryKey: queryKeys.xamplin.projects(),
    queryFn: () => xamplinApi.projects.list(),
  });

  const brands = (brandsQ.data?.items ?? []).filter((b) => b.companyType !== 'Agency');
  const projects = projectsQ.data?.items ?? [];
  const activeBrandId = selectedBrandId ?? brands[0]?.id ?? null;
  const visible = useMemo(
    () => (activeBrandId ? projects.filter((p) => p.companyId === activeBrandId) : projects),
    [projects, activeBrandId],
  );
  const activeBrandName = brands.find((b) => b.id === activeBrandId)?.name;
  const pg = usePagination(visible, 12);

  return (
    <div className="page flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]">
          <FolderOpen className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            Projects
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            {activeBrandName ? `${activeBrandName} · ` : ''}{visible.length} projects
          </p>
        </div>
      </div>

      {projectsQ.isLoading && (
        <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando proyectos…</div>
      )}
      {projectsQ.isError && (
        <div className="rounded-xl border border-[var(--color-error)] bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
          No se pudieron cargar los proyectos.
        </div>
      )}

      {!projectsQ.isLoading && !projectsQ.isError && visible.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[var(--color-xk-border)] px-6 py-10 text-center text-sm text-[var(--color-xk-text-muted)]">
          Esta marca aún no tiene proyectos.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {pg.pageItems.map((p) => (
          <Link
            key={p.id}
            to={`/projects/${p.id}`}
            className="group flex flex-col gap-2 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4 transition-colors hover:border-[var(--color-xk-accent)]"
          >
            <div className="flex items-start gap-2">
              <FolderOpen className="mt-0.5 h-4 w-4 flex-none text-[var(--color-xk-accent)]" />
              <span className="line-clamp-2 flex-1 text-sm font-semibold text-[var(--color-xk-lavender)]">
                {p.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {p.marketingObjectivePrimary && (
                <span className="mono rounded-full border border-[var(--color-xk-border)] px-2 py-0.5 text-[10px] text-[var(--color-xk-text-muted)]">
                  {p.marketingObjectivePrimary}
                </span>
              )}
              <span className="mono ml-auto text-[10px] text-[var(--color-xk-accent)] opacity-0 group-hover:opacity-100">
                Open planner →
              </span>
            </div>
          </Link>
        ))}
      </div>
      <Pagination {...pg} />
    </div>
  );
}
